import { CheckCircle2, ChevronRight, History, OctagonAlert, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { api, type DomainDetail, type DomainEvent, type ScheduledAction } from '../../../api';
import { Badge, type Tone } from '../../../components/Badge';
import { Section } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { EntityRef } from '../../../components/EntityRef';
import { CorrelationLink, PlainId } from '../../../components/Ids';
import { EmptyState, Loading } from '../../../components/States';
import { formatDateTime } from '../../../lib/format';
import { useLabels } from '../../../lib/labels';
import { useApi } from '../../../lib/useApi';
import { EnumBadge, humanize, WORKFLOW_TONE } from '../../ops/shared';
import { ActorName } from '../refs';
import { eventTone } from '../registration';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

const PAGE = 100;


const MARKER: Record<Tone, string> = {
  neutral: 'bg-muted-foreground',
  info: 'bg-info',
  ok: 'bg-success',
  pending: 'bg-info',
  warn: 'bg-warning',
  serious: 'bg-serious',
  crit: 'bg-critical',
};

function payloadString(payload: unknown, key: string): string | null {
  if (payload && typeof payload === 'object' && key in payload) {
    const v = (payload as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : null;
  }
  return null;
}

function EventLinks({ event: e }: { event: DomainEvent }) {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const tx = payloadString(e.payload, 'registry_transaction_id');
  const cltrid = payloadString(e.payload, 'cltrid');
  const wf = payloadString(e.payload, 'workflow_id');
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      {e.correlation_id ? (
        <li>
          <CorrelationLink id={e.correlation_id} label={t('history.correlation')} n={13} />
        </li>
      ) : null}
      {wf ? (
        <li>
          <EntityRef label={e.workflow_type ? L.workflowType(e.workflow_type) : t('history.workflow')} to={`/ops/workflows/${encodeURIComponent(wf)}`} id={wf} />
        </li>
      ) : null}
      {tx ? (
        <li>
          <EntityRef label={cltrid ? t('history.registryTxTrid', { cltrid }) : t('history.registryTx')} to={`/registry/transactions/${encodeURIComponent(tx)}`} id={tx} />
        </li>
      ) : null}
      {e.request_id ? (
        <li className="inline-flex items-center gap-1 text-muted-foreground">
          {t('history.request')} <PlainId id={e.request_id} />
        </li>
      ) : null}
    </ul>
  );
}

/** `history.verify_domain_chain` problem ("event_hash mismatch") -> a sentence staff can act on. */
function useChainProblem(): (problem: string) => string {
  const { t } = useTranslation('domains');
  const tr = t as unknown as Lookup;
  return (problem) => {
    const key = problem.trim().replace(/[\s.-]+/g, '_').toUpperCase();
    return tr(`history.chainProblem.${key}`, { defaultValue: tr('history.chainProblem.other') });
  };
}

/**
 * "Thao tác đang chờ" (owner 2026-10-07): the domain's operations still in the queue — workflows
 * PENDING / RUNNING / WAITING / RETRYING — with their step and next run, refreshed every 5 s
 * while any is there — and the scheduled actions not yet run (e.g. the renewal flag's release).
 */
function PendingOperations({ domainId, scheduled }: { domainId: string; scheduled: readonly ScheduledAction[] }) {
  const { t } = useTranslation('domains');
  const queue = useApi((signal) => api.listWorkflows({ state: 'ACTIVE', resource_id: domainId, limit: 20 }, { signal }), [domainId]);
  const items = queue.status === 'success' ? queue.data.items : [];
  const { reload } = queue;
  useEffect(() => {
    if (items.length === 0) return undefined;
    const timer = window.setInterval(reload, 5000);
    return () => window.clearInterval(timer);
  }, [items.length, reload]);
  return (
    <div className="rounded-md border px-3 py-2.5" data-pending-operations>
      <p className="text-sm font-medium">{t('history.queue.title')}</p>
      {queue.status === 'error' ? (
        <DataError error={queue.error} onRetry={reload} />
      ) : items.length === 0 && scheduled.length === 0 ? (
        <p className="mt-1 text-[13px] text-muted-foreground">{queue.status === 'loading' ? t('history.queue.loading') : t('history.queue.empty')}</p>
      ) : (
        <ul className="mt-2 grid gap-2">
          {scheduled.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm" data-scheduled-action={a.action_type}>
              <span className="font-medium" title={a.action_type}>
                {t(`history.queue.actions.${a.action_type}`, { defaultValue: humanize(a.action_type) })}
              </span>
              <Badge tone={a.status === 'CLAIMED' ? 'info' : 'pending'} icon={false}>
                {a.status === 'CLAIMED' ? t('history.queue.running') : t('history.queue.scheduledBadge')}
              </Badge>
              <span className="text-muted-foreground">{t('history.queue.scheduled', { when: formatDateTime(a.run_at) })}</span>
            </li>
          ))}
          {items.map((w) => (
            <li key={w.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <EntityRef label={humanize(w.workflow_type)} to={`/ops/workflows/${encodeURIComponent(w.id)}`} id={w.id} />
              <EnumBadge group="workflowState" value={w.state} tones={WORKFLOW_TONE} />
              {w.locked_by ? <Badge tone="info" icon={false}>{t('history.queue.running')}</Badge> : null}
              <span className="text-muted-foreground">
                {t('history.queue.step')}: <code className="text-xs">{w.current_step ?? '—'}</code>
              </span>
              {w.next_run_at ? <span className="text-muted-foreground">{t('history.queue.next', { when: formatDateTime(w.next_run_at) })}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function HistoryTab({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('domains');
  const L = useLabels();
  const chainProblem = useChainProblem();
  const key = detail.domain.id;
  const first = useApi((signal) => api.getDomainTimeline(key, { limit: PAGE }, { signal }), [key]);
  const [older, setOlder] = useState<DomainEvent[]>([]);
  const [olderFor, setOlderFor] = useState(key);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const [exhausted, setExhausted] = useState(false);
  const [filter, setFilter] = useState('');
  const { hash } = useLocation();
  const target = hash ? decodeURIComponent(hash.slice(1)) : '';

  // A different domain resets the pages loaded so far.
  if (olderFor !== key) {
    setOlderFor(key);
    setOlder([]);
    setExhausted(false);
  }

  useEffect(() => {
    if (first.status === 'success' && target) document.getElementById(target)?.scrollIntoView({ block: 'center' });
  }, [first.status, target]);

  if (first.status === 'loading') return <Loading label={t('history.loading')} rows={4} />;
  if (first.status === 'error') return <DataError error={first.error} onRetry={first.reload} title={t('history.loadFailed')} />;

  const { chain_intact, chain_breaks } = first.data;
  const events = [...first.data.events, ...older];
  const broken = new Map(chain_breaks.map(([seq, problem]) => [seq, problem]));
  const lowest = events.reduce((m, e) => Math.min(m, e.sequence), Number.POSITIVE_INFINITY);
  const canLoadMore = !exhausted && first.data.events.length === PAGE && lowest > 1;
  const q = filter.trim().toUpperCase();
  const shown = q
    ? events.filter((e) => e.event_type.toUpperCase().includes(q) || L.eventType(e.event_type).toUpperCase().includes(q) || e.correlation_id?.toUpperCase().includes(q))
    : events;

  const loadMore = async () => {
    setLoadingMore(true);
    setMoreError(null);
    try {
      const page = await api.getDomainTimeline(key, { before: lowest, limit: PAGE });
      setOlder((prev) => [...prev, ...page.events]);
      if (page.events.length < PAGE) setExhausted(true);
    } catch (err) {
      setMoreError(err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Section
      title={t('history.title')}
      description={t('history.description')}
      actions={
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('history.filterPlaceholder')}
            aria-label={t('history.filter')}
            className="h-8 pl-8"
          />
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-5">
        <PendingOperations domainId={key} scheduled={first.status === 'success' ? (first.data.pending_actions ?? []) : []} />
        {chain_intact ? (
          <p className="flex items-start gap-2 rounded-md border border-success/25 bg-success-soft px-3 py-2 text-sm" role="status">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" /> {t('history.intact')}
          </p>
        ) : (
          <div className="flex gap-3 rounded-md border border-critical/30 bg-critical-soft p-3 text-sm" role="alert">
            <OctagonAlert className="mt-0.5 size-4 shrink-0 text-critical" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <strong className="font-semibold">{t('history.broken', { count: chain_breaks.length })}</strong>
              <p>{t('history.brokenBody')}</p>
              <ul className="list-disc pl-5 text-xs">
                {chain_breaks.map(([seq, problem]) => (
                  <li key={seq}>
                    #{seq}: {chainProblem(problem)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {shown.length === 0 ? (
          <EmptyState title={t('history.noEvents')} icon={History}>
            {q ? t('history.noEventsFiltered') : null}
          </EmptyState>
        ) : (
          <ol className="relative">
            {shown.map((e, i) => {
              const isBroken = broken.has(e.sequence);
              const tone = isBroken ? 'crit' : eventTone(e.event_type);
              const isTarget = e.id === target;
              return (
                <li key={e.id} id={e.id} className="relative flex gap-4 pb-5 last:pb-0">
                  {i < shown.length - 1 ? <span className="absolute top-4 bottom-0 left-[5px] w-px bg-muted-foreground/25" aria-hidden="true" /> : null}
                  <span className={cn('relative mt-1.5 size-[11px] shrink-0 rounded-full ring-4 ring-card', MARKER[tone])} aria-hidden="true" />
                  <div className={cn('min-w-0 flex-1 flex flex-col gap-1.5 rounded-md', isTarget && '-m-2 bg-accent p-2 ring-2 ring-primary/40')}>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <time dateTime={e.occurred_at} className="tabular">
                        {formatDateTime(e.occurred_at)}
                      </time>
                      <Badge tone="neutral" icon={false}>
                        #{e.sequence}
                      </Badge>
                      {isBroken ? <Badge tone="crit">{t('history.mismatch')}</Badge> : null}
                    </div>
                    <p className="text-sm">
                      <span className="font-semibold">{L.eventType(e.event_type)}</span>
                      {e.event_version > 1 ? <span className="text-xs text-muted-foreground"> v{e.event_version}</span> : null}
                    </p>
                    <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                      <ActorName type={e.actor_type} name={e.actor_name} />
                      {e.source && !(e.actor_type === 'SYSTEM' && e.actor_name === e.source) ? <> · {t('history.via', { source: L.source(e.source) })}</> : null}
                      {e.initiated_by ? (
                        <>
                          {' · '}
                          {t('history.requestedBy')} <ActorName type={e.initiated_by.actor_type} name={e.initiated_by.actor_name} />
                        </>
                      ) : null}
                    </p>
                    <EventLinks event={e} />
                    {e.payload && typeof e.payload === 'object' && Object.keys(e.payload).length ? (
                      <details className="group text-xs">
                        <summary className="inline-flex cursor-pointer list-none items-center gap-1 font-medium text-muted-foreground hover:text-foreground">
                          <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" aria-hidden="true" />
                          {t('history.payload')}
                        </summary>
                        <pre className="mt-2 max-h-80 overflow-auto rounded-md bg-muted p-3 font-mono text-xs">{JSON.stringify(e.payload, null, 2)}</pre>
                      </details>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {moreError ? (
          <div>
            <DataError error={moreError} onRetry={loadMore} title={t('history.olderFailed')} />
          </div>
        ) : null}
        {canLoadMore ? (
          <div className="flex justify-center">
            <Button type="button" variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? t('history.loadingOlder') : t('history.loadOlder')}
            </Button>
          </div>
        ) : null}
      </div>
    </Section>
  );
}
